import { Injectable, Injector } from '@angular/core';

import { ReplaySubject, Subject } from 'rxjs';
import {
  AlfrescoApiService,
  NodesApiService,
} from '@alfresco/adf-content-services';
import {
  AlfrescoApi,
  ClassesApi,
  ContentApi,
  FavoritesApi,
  GroupsApi,
  Node,
  NodesApi,
  PeopleApi,
  SearchApi,
  SharedlinksApi,
  SitesApi,
  VersionsApi,
} from '@alfresco/js-api';

import { RequestParams } from '../types';

@Injectable({
  providedIn: 'root',
})
export class WkAlfrescoApiService {
  private readonly apiService: AlfrescoApiService;
  private readonly nodesApiService: NodesApiService;

  private _contentApi: ContentApi;
  public get contentApi(): ContentApi {
    if (!this._contentApi) {
      this._contentApi = new ContentApi(this.apiService.getInstance());
    }

    return this._contentApi;
  }

  private _nodesApi: NodesApi;
  public get nodesApi(): NodesApi {
    if (!this._nodesApi) {
      this._nodesApi = new NodesApi(this.apiService.getInstance());
    }

    return this._nodesApi;
  }

  private _sharedLinksApi: SharedlinksApi;
  public get sharedLinksApi(): SharedlinksApi {
    if (!this._sharedLinksApi) {
      this._sharedLinksApi = new SharedlinksApi(this.apiService.getInstance());
    }

    return this._sharedLinksApi;
  }

  private _peopleApi: PeopleApi;
  public get peopleApi(): PeopleApi {
    if (!this._peopleApi) {
      this._peopleApi = new PeopleApi(this.apiService.getInstance());
    }

    return this._peopleApi;
  }

  private _groupsApi: GroupsApi;
  public get groupsApi(): GroupsApi {
    if (!this._groupsApi) {
      this._groupsApi = new GroupsApi(this.apiService.getInstance());
    }

    return this._groupsApi;
  }

  private _favoritesApi: FavoritesApi;
  public get favoritesApi(): FavoritesApi {
    if (!this._favoritesApi) {
      this._favoritesApi = new FavoritesApi(this.apiService.getInstance());
    }

    return this._favoritesApi;
  }

  private _searchApi: SearchApi;
  public get searchApi(): SearchApi {
    if (!this._searchApi) {
      this._searchApi = new SearchApi(this.apiService.getInstance());
    }

    return this._searchApi;
  }

  private _sitesApi: SitesApi;
  public get sitesApi(): SitesApi {
    if (!this._sitesApi) {
      this._sitesApi = new SitesApi(this.apiService.getInstance());
    }

    return this._sitesApi;
  }

  private _classesApi: ClassesApi;
  public get classesApi(): ClassesApi {
    if (!this._classesApi) {
      this._classesApi = new ClassesApi(this.apiService.getInstance());
    }

    return this._classesApi;
  }

  private _versionsApi: VersionsApi;
  public get versionsApi(): VersionsApi {
    if (!this._versionsApi) {
      this._versionsApi = new VersionsApi(this.apiService.getInstance());
    }

    return this._versionsApi;
  }

  public get alfrescoApiInitialized(): ReplaySubject<boolean> {
    return this.apiService.alfrescoApiInitialized;
  }

  public get nodeUpdated(): Subject<Node> {
    return this.nodesApiService.nodeUpdated;
  }

  constructor(injector: Injector) {
    this.apiService = injector.get<AlfrescoApiService>(AlfrescoApiService);
    this.nodesApiService = injector.get<NodesApiService>(NodesApiService);
  }

  public callProcessClientCustomApi(requestParam: RequestParams): Promise<any> {
    const processInstance = this.getInstance().processClient;
    return this.callCustomApiByMethod(processInstance, requestParam);
  }

  public callContentClientCustomApi(requestParam: RequestParams): Promise<any> {
    const clientInstance = this.getInstance().contentClient;
    return this.callCustomApiByMethod(clientInstance, requestParam);
  }

  public getInstance(): AlfrescoApi {
    return this.apiService.getInstance();
  }

  private callCustomApiByMethod(
    instance: any,
    params: RequestParams
  ): Promise<any> {
    params.contextRoot = params.path;
    params.path = '';
    switch (params.httpMethod.toUpperCase()) {
      case 'GET':
        return instance.get(params);
      case 'POST':
        return instance.post(params);
      case 'PUT':
        return instance.put(params);
      case 'DELETE':
        return instance.delete(params);
      default:
        throw new Error('Invalid HTTP method');
    }
  }
}
